import { type ReactElement, useContext } from "react";
import { BarChart3, ChevronDown, Layers } from "lucide-react";
import { ResponsiveContainer } from "recharts";
import { ChartPreviewContext } from "./ChartPreviewContext";

interface MetricChartFrameProps {
  /** Chart element (a single child, as ResponsiveContainer requires). */
  children: ReactElement;
  /** Plain-text takeaway — also the chart's accessible description / SEO text. */
  caption: string;
  /** Chart-type pill label. */
  chartType?: string;
  /** Breakdown pill label. */
  breakdown?: string;
  /** Granularity pill label. */
  granularity?: string;
  /** Time-range segments; `active` is highlighted. */
  ranges?: string[];
  active?: string;
  height?: number;
  /** Render children directly (no ResponsiveContainer) — for non-Recharts content like a cohort table. */
  raw?: boolean;
  /** Hide the left toolbar pills (chart type + breakdown) when they don't apply. */
  hideControls?: boolean;
}

const PILL =
  "flex items-center gap-x-2 whitespace-nowrap rounded-lg border border-border bg-card px-2 py-1.5 text-sm text-foreground";

const DEFAULT_RANGES = ["Custom", "7D", "1M", "3M", "6M", "1Y"];

/**
 * Wraps a chart in a faithful, static reproduction of the Bigdelta app's metric
 * widget, laid out as a SINGLE cohesive card: a header row (chart type /
 * breakdown / time range), the chart body, and a caption footer, divided by
 * hairline borders. The chrome is decorative (`aria-hidden`); the chart's
 * meaning is carried by the visible <figcaption>, which is also the crawlable
 * SEO text since Recharts SVG isn't reliably indexed. No `role="img"` on the
 * figure, so the figcaption stays in the accessibility tree rather than being
 * collapsed.
 *
 * Built on the site's CSS-variable tokens, so it tracks light/dark automatically
 * rather than hardcoding the product's light-only palette.
 */
const MetricChartFrame = ({
  children,
  caption,
  chartType = "Bar",
  breakdown = "No breakdown",
  granularity = "Month",
  ranges = DEFAULT_RANGES,
  active = "1Y",
  height = 360,
  raw = false,
  hideControls = false,
}: MetricChartFrameProps) => {
  const preview = useContext(ChartPreviewContext);

  // Thumbnail mode: just the chart body, filling its container, no chrome.
  // The `chart-preview` class hides bar value labels (see index.css) so tiny
  // previews stay clean.
  if (preview) {
    return (
      <div className="chart-preview h-full w-full">
        {raw ? (
          children
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        )}
      </div>
    );
  }

  return (
    <figure className="my-8 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {/* Header toolbar — decorative reproduction of the app controls. */}
      <div
        aria-hidden
        className="flex items-center justify-between gap-2 border-b border-border px-4 py-3"
      >
        <div className="flex flex-shrink-0 gap-x-2">
          {!hideControls && (
            <>
              <span className={PILL}>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                {chartType}
              </span>
              <span className={PILL}>
                <Layers className="h-4 w-4 text-muted-foreground" />
                {breakdown}
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </span>
            </>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-x-2">
          <span className={PILL}>{granularity}</span>
          <div className="flex gap-x-px rounded-lg bg-border p-px">
            {ranges.map((range) => (
              <span
                key={range}
                className={`px-3 py-1.5 text-sm first:rounded-l-lg last:rounded-r-lg ${
                  range === active ? "bg-muted text-foreground" : "bg-card text-muted-foreground"
                }`}
              >
                {range}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Chart body. */}
      <div style={raw ? undefined : { height }} className="w-full p-6">
        {raw ? (
          children
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        )}
      </div>

      {/* Caption footer. */}
      <figcaption className="border-t border-border px-6 py-4 text-sm text-muted-foreground leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  );
};

export default MetricChartFrame;
